<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcContactList',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    parent: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const items = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    items: items.value,
    ux: ux.value,
  }));

  const $$load_items = () => {
    return props.parent.contacts.data;
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'items' }, { name: 'ux' }],
    loaders: { items: $$load_items },
    ux: {},
    dataRefs: { items, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.parent,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('parent');
    }
  );

  const act_2095089031469157_showModal_8r6o398y0_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'WContactModal',
      props: {
        parent: props.parent,
      },
    };
  };
  const act_294572930540096_showModal_pxfjl2tkt_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'WContactModal',
      props: {
        parent: props.parent,
        item: $event.scope.item,
      },
    };
  };
  const act_3365375699802249_rest_i5mmujo4p_getActionArgs = async ($event) => {
    return {
      method: 'delete',
      container: props.parent.contacts,
      resource: $event.scope.item,
    };
  };
  const act_3365375699802249_rest_i5mmujo4p_getConfirm = async ($event) => {
    return {
      message: $fn.value.gettext('Please confirm delete.'),
      btnOk: $fn.value.gettext('Delete'),
    };
  };

  const act_7315092382398562_reloadComponentData_pwovgrgdr =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_g6nbxjcrx =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_pwovgrgdr,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_g6nbxjcrx],
    event: 'reload',
  });
  const act_2095089031469157_showModal_8r6o398y0 =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_8r6o398y0_getActionArgs,
      displayName: 'create',
    });
  const inst_evh_2095089031469157_clickPreventStop_edd6zfykx =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_8r6o398y0,
      event: 'click.prevent.stop',
      displayName: 'create',
    });
  const inst_evh_2095089031469157_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2095089031469157_clickPreventStop_edd6zfykx],
      event: 'click.prevent.stop',
    });
  const act_294572930540096_showModal_pxfjl2tkt =
    new actionTypes.ShowModalAction({
      actionArgs: act_294572930540096_showModal_pxfjl2tkt_getActionArgs,
    });
  const inst_evh_294572930540096_clickPreventStop_aajsgfoub =
    new actionTypes.EventHandlerImpl({
      action: act_294572930540096_showModal_pxfjl2tkt,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_294572930540096_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_294572930540096_clickPreventStop_aajsgfoub],
      event: 'click.prevent.stop',
    });
  const act_3365375699802249_rest_i5mmujo4p = new actionTypes.RestAction({
    actionArgs: act_3365375699802249_rest_i5mmujo4p_getActionArgs,
    confirm: act_3365375699802249_rest_i5mmujo4p_getConfirm,
  });
  const inst_evh_3365375699802249_clickPreventStop_ff53ses5q =
    new actionTypes.EventHandlerImpl({
      action: act_3365375699802249_rest_i5mmujo4p,
      event: 'click.prevent.stop',
      displayName: 'rest',
    });
  const inst_evh_3365375699802249_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3365375699802249_clickPreventStop_ff53ses5q],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_reload_g6nbxjcrx,
    inst_evh_7315092382398562_reload,
    inst_evh_2095089031469157_clickPreventStop_edd6zfykx,
    create: inst_evh_2095089031469157_clickPreventStop_edd6zfykx,
    inst_evh_2095089031469157_clickPreventStop,
    inst_evh_294572930540096_clickPreventStop_aajsgfoub,
    inst_evh_294572930540096_clickPreventStop,
    inst_evh_3365375699802249_clickPreventStop_ff53ses5q,
    inst_evh_3365375699802249_clickPreventStop,
  };

  const evh_7315092382398562_reload_g6nbxjcrx = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_g6nbxjcrx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop_edd6zfykx = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop_edd6zfykx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_294572930540096_clickPreventStop_aajsgfoub = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_294572930540096_clickPreventStop_aajsgfoub.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_294572930540096_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_294572930540096_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3365375699802249_clickPreventStop_ff53ses5q = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3365375699802249_clickPreventStop_ff53ses5q.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3365375699802249_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_3365375699802249_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpWidgetCard>
      <template #header>
        <ItpBox alignItems="center">
          <strong>
            {{ gettext('Contacts') }}
          </strong>
          <ItpButton
            :text="gettext('Add contact...')"
            variant="light"
            size="sm"
            class="ms-auto"
            v-if="!parent.isReadOnly"
            @click.prevent.stop="
              evh_2095089031469157_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </template>
      <BListGroup>
        <BListGroupItem v-if="!items || !items.length">
          <span>
            {{ gettext('No contacts have been added yet.') }}
          </span>
        </BListGroupItem>
        <BListGroupItem
          :key="item.id"
          class="d-flex flex-column"
          v-for="item in _.orderBy(items, ['created_at'], ['desc'])"
        >
          <ItpBox spacing="pb-1">
            <UcContactCard :item="item" class="mb-2"> </UcContactCard>
            <BDropdown
              size="sm"
              toggle-class="btn-default-borderless"
              no-caret
              variant="none"
              class="ms-auto mb-auto"
              v-if="!item.locked"
            >
              <template #button-content>
                <ItpIcon fa="ellipsis-v" class="mx-1"> </ItpIcon>
              </template>
              <BDropdownItem
                @click.prevent.stop="
                  evh_294572930540096_clickPreventStop($event, { item })
                "
              >
                <ItpText :text="pgettext('Command', 'Edit')"> </ItpText>
              </BDropdownItem>
              <BDropdownItem
                v-if="!item.locked"
                @click.prevent.stop="
                  evh_3365375699802249_clickPreventStop($event, { item })
                "
              >
                <ItpText :text="pgettext('Command', 'Delete')"> </ItpText>
              </BDropdownItem>
            </BDropdown>
          </ItpBox>
          <div class="d-flex align-items-end">
            <ItpText muted small>
              {{ datetime(item.created_at) }}
            </ItpText>
            <small class="text-muted text-nowrap mx-2">
              {{ username(item.updated_by || item.created_by) }}
            </small>
            <div
              class="ms-2 d-flex align-self-end flex-grow-1 justify-content-end"
            >
              <ItpIcon
                :title="gettext('Locked')"
                name="fa-lock"
                class="text-muted"
                v-if="item.locked"
              >
              </ItpIcon>
            </div>
          </div>
        </BListGroupItem>
      </BListGroup>
    </ItpWidgetCard>
  </ItpUsercontrol>
</template>
